/**
 * NotebookBubbleMenu — the Notion-style floating toolbar that appears
 * above a non-empty text selection. Provides one-click access to
 * formatting (bold / italic / strike / code), inline link, and the
 * "Turn into" menu (paragraph / h1-h3 / list / quote / code block).
 *
 * Built on @tiptap/react's `BubbleMenu` (the React component replaces
 * the legacy `BubbleMenu` extension — see tiptap.dev migration guide).
 * The component receives the editor instance and renders into a
 * tippy.js-anchored portal managed internally by BubbleMenu — DO NOT
 * wrap this in `createPortal(..., document.body)` as that creates a
 * double-portal: the outer React portal + the BubbleMenu's internal
 * `ReactRenderer` portal. On editor unmount, the two portals fight
 * over the same DOM nodes and React throws `NotFoundError:
 * Failed to execute 'removeChild'` (which is what destroyed the page
 * on delete). Let BubbleMenu handle its own positioning.
 *
 * Active state: each button reflects `editor.isActive(mark|node)` so
 * the user always sees which formats are applied to the current
 * selection. Disabled state: formatting buttons are disabled when the
 * selection is empty / collapsed.
 */

import { BubbleMenu } from '@tiptap/react'
import type { Editor } from '@tiptap/react'
import { useState } from 'react'
import { turnIntoTable } from '@/lib/tiptap/tableExtension'
import { cn } from '@/lib/utils'

export interface NotebookBubbleMenuProps {
  editor: Editor | null
}

export function NotebookBubbleMenu({ editor }: NotebookBubbleMenuProps) {
  const [linkOpen, setLinkOpen] = useState(false)
  const [linkValue, setLinkValue] = useState('')
  const [turnIntoOpen, setTurnIntoOpen] = useState(false)

  if (!editor) return null

  const isEmpty = editor.state.selection.empty

  const setLink = () => {
    const prev = editor.getAttributes('link').href ?? ''
    setLinkValue(prev)
    setLinkOpen(true)
  }

  const commitLink = () => {
    const url = linkValue.trim()
    if (!url) {
      editor.chain().focus().extendMarkRange('link').unsetLink().run()
    } else {
      editor.chain().focus().extendMarkRange('link').setLink({ href: url }).run()
    }
    setLinkOpen(false)
  }

  return (
    <BubbleMenu
      editor={editor}
      updateDelay={50}
      shouldShow={({ editor: e, from, to }) => {
        // Hide on collapsed selection (just a cursor) and inside code blocks.
        if (from === to) return false
        if (e.isActive('codeBlock')) return false
        return true
      }}
      className="z-[80] flex items-center gap-0.5 rounded-lg border border-surface-200 bg-white px-1 py-1 shadow-xl shadow-black/10 dark:border-surface-700 dark:bg-surface-800"
    >
      <Btn
        onClick={() => editor.chain().focus().toggleBold().run()}
        active={editor.isActive('bold')}
        disabled={isEmpty}
        title="Bold (⌘B)"
      >
        <span className="font-bold">B</span>
      </Btn>
      <Btn
        onClick={() => editor.chain().focus().toggleItalic().run()}
        active={editor.isActive('italic')}
        disabled={isEmpty}
        title="Italic (⌘I)"
      >
        <span className="italic">I</span>
      </Btn>
      <Btn
        onClick={() => editor.chain().focus().toggleStrike().run()}
        active={editor.isActive('strike')}
        disabled={isEmpty}
        title="Strikethrough"
      >
        <span className="line-through">S</span>
      </Btn>
      <Btn
        onClick={() => editor.chain().focus().toggleCode().run()}
        active={editor.isActive('code')}
        disabled={isEmpty}
        title="Inline code (⌘E)"
      >
        <span className="font-mono text-[10px]">{'<>'}</span>
      </Btn>
      <Sep />
      <Btn onClick={setLink} active={editor.isActive('link')} disabled={isEmpty} title="Add link">
        <LinkIcon className="h-3.5 w-3.5" />
      </Btn>
      <Sep />
      <div className="relative">
        <Btn
          onClick={() => setTurnIntoOpen((v) => !v)}
          active={turnIntoOpen}
          title="Turn into"
        >
          <span className="text-[10px]">H1</span>
          <ChevronDown className="h-2.5 w-2.5 opacity-60" />
        </Btn>
        {turnIntoOpen && (
          <TurnIntoMenu
            editor={editor}
            onClose={() => setTurnIntoOpen(false)}
          />
        )}
      </div>

      {linkOpen && (
        <div className="ml-1 flex items-center gap-1 border-l border-surface-200 pl-2 dark:border-surface-700">
          <input
            autoFocus
            type="url"
            value={linkValue}
            onChange={(e) => setLinkValue(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                e.preventDefault()
                commitLink()
              } else if (e.key === 'Escape') {
                e.preventDefault()
                setLinkOpen(false)
              }
            }}
            placeholder="https://"
            className="h-7 w-48 rounded border border-surface-200 bg-white px-2 text-xs outline-none focus:border-brand-500 dark:border-surface-700 dark:bg-surface-900"
          />
          <button
            type="button"
            onClick={commitLink}
            className="h-7 rounded bg-brand-500 px-2 text-xs font-medium text-white hover:bg-brand-600"
          >
            Save
          </button>
          <button
            type="button"
            onClick={() => setLinkOpen(false)}
            className="h-7 rounded px-2 text-xs text-surface-500 hover:bg-surface-100 dark:hover:bg-surface-700"
          >
            Cancel
          </button>
        </div>
      )}
    </BubbleMenu>
  )
}

function Btn({
  onClick,
  active,
  disabled,
  title,
  children,
}: {
  onClick: () => void
  active?: boolean
  disabled?: boolean
  title: string
  children: React.ReactNode
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      title={title}
      className={cn(
        'inline-flex h-7 min-w-[28px] items-center justify-center gap-0.5 rounded px-1.5 text-xs text-surface-700 transition-colors',
        'hover:bg-surface-100 dark:text-surface-200 dark:hover:bg-surface-700',
        active && 'bg-brand-50 text-brand-700 dark:bg-brand-900/40 dark:text-brand-200',
        disabled && 'cursor-not-allowed opacity-40 hover:bg-transparent dark:hover:bg-transparent',
      )}
    >
      {children}
    </button>
  )
}

function Sep() {
  return <span className="mx-0.5 h-4 w-px bg-surface-200 dark:bg-surface-700" />
}

function TurnIntoMenu({ editor, onClose }: { editor: Editor; onClose: () => void }) {
  const items: Array<{ label: string; run: () => void; isActive: () => boolean }> = [
    {
      label: 'Text',
      run: () => editor.chain().focus().setParagraph().run(),
      isActive: () => editor.isActive('paragraph'),
    },
    {
      label: 'Heading 1',
      run: () => editor.chain().focus().toggleHeading({ level: 1 }).run(),
      isActive: () => editor.isActive('heading', { level: 1 }),
    },
    {
      label: 'Heading 2',
      run: () => editor.chain().focus().toggleHeading({ level: 2 }).run(),
      isActive: () => editor.isActive('heading', { level: 2 }),
    },
    {
      label: 'Heading 3',
      run: () => editor.chain().focus().toggleHeading({ level: 3 }).run(),
      isActive: () => editor.isActive('heading', { level: 3 }),
    },
    {
      label: 'Bulleted list',
      run: () => editor.chain().focus().toggleBulletList().run(),
      isActive: () => editor.isActive('bulletList'),
    },
    {
      label: 'Numbered list',
      run: () => editor.chain().focus().toggleOrderedList().run(),
      isActive: () => editor.isActive('orderedList'),
    },
    {
      label: 'Todo list',
      run: () => editor.chain().focus().toggleTaskList().run(),
      isActive: () => editor.isActive('taskList'),
    },
    {
      label: 'Quote',
      run: () => editor.chain().focus().toggleBlockquote().run(),
      isActive: () => editor.isActive('blockquote'),
    },
    {
      label: 'Code block',
      run: () => editor.chain().focus().toggleCodeBlock().run(),
      isActive: () => editor.isActive('codeBlock'),
    },
    {
      label: 'Table',
      run: () => turnIntoTable(editor),
      isActive: () => editor.isActive('table'),
    },
  ]

  return (
    <>
      {/* Click-away backdrop */}
      <div className="fixed inset-0 z-30" onClick={onClose} />
      <div className="absolute left-0 top-full z-40 mt-1 w-44 overflow-hidden rounded-md border border-surface-200 bg-white py-1 shadow-xl shadow-black/10 dark:border-surface-700 dark:bg-surface-800">
        {items.map((it) => (
          <button
            key={it.label}
            type="button"
            onClick={() => {
              it.run()
              onClose()
            }}
            className={cn(
              'flex w-full items-center justify-between px-3 py-1.5 text-left text-xs text-surface-700 hover:bg-surface-100 dark:text-surface-200 dark:hover:bg-surface-700',
              it.isActive() && 'bg-brand-50 text-brand-700 dark:bg-brand-900/40 dark:text-brand-200',
            )}
          >
            <span>{it.label}</span>
            {it.isActive() && <span className="text-brand-500">✓</span>}
          </button>
        ))}
      </div>
    </>
  )
}

function LinkIcon({ className }: { className?: string }) {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className={className}>
      <path d="M10 13a5 5 0 007.54.54l3-3a5 5 0 00-7.07-7.07l-1.72 1.71" />
      <path d="M14 11a5 5 0 00-7.54-.54l-3 3a5 5 0 007.07 7.07l1.71-1.71" />
    </svg>
  )
}

function ChevronDown({ className }: { className?: string }) {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className={className}>
      <polyline points="6 9 12 15 18 9" />
    </svg>
  )
}
